import {
  assertNever,
  EnrichmentRunState,
  ProspectListEnrichmentState,
  ProspectListStatus,
  type EnrichmentBatchProgress,
  type EnrichmentRunRead,
  type ProspectListRead,
} from '@signalvote/shared';
import { isTerminalStatus } from './prospectListUpload';

export const NEVER_RUN: EnrichmentRunRead = {
  state: EnrichmentRunState.NeverRun,
  startedAt: null,
  finishedAt: null,
  summary: null,
  error: null,
  progress: null,
};

/** Enriching an upload's people is still to come or under way. */
export function uploadEnrichmentPending(upload: ProspectListRead): boolean {
  const state = upload.enrichment?.state;
  switch (state) {
    case undefined:
    case ProspectListEnrichmentState.Completed:
    case ProspectListEnrichmentState.Failed:
    case ProspectListEnrichmentState.NotRun:
      return false;
    case ProspectListEnrichmentState.Waiting:
      // Waiting only means something while the upload can still finish.
      return !isTerminalStatus(upload.status);
    case ProspectListEnrichmentState.Running:
      return true;
    default:
      return assertNever(state);
  }
}

/** The upload is still adding people, or its people are still being enriched. */
export function uploadInProgress(upload: ProspectListRead): boolean {
  return !isTerminalStatus(upload.status) || uploadEnrichmentPending(upload);
}

/**
 * The campaign run is the upload's own Enrichment, so its stages are the upload's. The API says so: it
 * shows an upload's Enrichment as the campaign's run unless another run was already going.
 */
export function isUploadsRun(upload: ProspectListRead): boolean {
  return upload.enrichment?.isCampaignRun === true;
}

/**
 * The upload to follow when the page opens: the newest one still adding people, or else the newest one
 * whose Enrichment started with the run now going (it hands over as that run starts, or while another is
 * already going). An earlier upload never claims a later run.
 */
export function uploadToResume(lists: ProspectListRead[], run: EnrichmentRunRead): ProspectListRead | undefined {
  const adding = lists.find((l) => !isTerminalStatus(l.status));
  if (adding || run.state !== EnrichmentRunState.Running || !run.startedAt) return adding;
  const newest = lists[0];
  return newest?.enrichmentTriggeredAt && Date.parse(newest.enrichmentTriggeredAt) >= Date.parse(run.startedAt)
    ? newest
    : undefined;
}

/** An upload that finished with nobody new: the stages have nothing to show, so it skips to the result. */
export function nobodyNew(upload: ProspectListRead): boolean {
  return upload.status === ProspectListStatus.Imported && upload.createdCount === 0;
}

/**
 * How far along the work is, as a whole percent. Adding people is the first half and matching them the
 * second; a run on its own (no upload) is the whole bar.
 */
export function workPercent(run: EnrichmentRunRead, upload: ProspectListRead | null): number {
  const matched = run.progress && run.progress.peopleTotal > 0 ? run.progress.peopleProcessed / run.progress.peopleTotal : 0;
  if (!upload) return Math.round(matched * 100);
  if (!isTerminalStatus(upload.status)) {
    return Math.round((upload.rowCount > 0 ? upload.rowsWatermark / upload.rowCount : 0) * 50);
  }
  return Math.round(50 + (isUploadsRun(upload) && run.state === EnrichmentRunState.Running ? matched : 0) * 50);
}

/** How far matching has got, in the counts the progress banner shows. */
export interface MatchingCounts {
  checked: number;
  total: number;
  matched: number;
  toReview: number;
  noMatch: number;
  waiting: number;
}

/**
 * The work behind a closed overlay, as the page's banner says it: whose people are being added, or whose are
 * being matched against federal records, and — while matching — the counts so far. Never a batch number:
 * batches are how the matcher paces itself, not something a Fundraiser acts on.
 */
export function backgroundWork(
  run: EnrichmentRunRead,
  upload: ProspectListRead | null,
): { headline: string; counts: MatchingCounts | null } {
  const n = (value: number) => value.toLocaleString();
  if (upload && !isTerminalStatus(upload.status)) {
    return { headline: `Adding people from ${upload.originalFileName} · ${n(upload.rowsWatermark)} of ${n(upload.rowCount)}`, counts: null };
  }
  const whose = upload ? upload.originalFileName : 'your book';
  const progress = run.progress;
  const followsRun = !upload || isUploadsRun(upload);
  if (followsRun && run.state === EnrichmentRunState.Running && progress) {
    const { batches } = progress;
    const sum = (pick: (b: EnrichmentBatchProgress) => number) => batches.reduce((total, b) => total + pick(b), 0);
    return {
      headline: `Matching ${whose} against federal records`,
      counts: {
        checked: progress.peopleProcessed,
        total: progress.peopleTotal,
        matched: sum((b) => b.peopleWithAnyMatch),
        toReview: sum((b) => b.peopleNeedingReview),
        noMatch: sum((b) => Math.max(0, b.people - b.peopleWithAnyMatch - b.peopleNeedingReview - b.peopleFailed)),
        waiting: Math.max(0, progress.peopleTotal - progress.peopleProcessed),
      },
    };
  }
  return { headline: `Matching ${whose} against federal records`, counts: null };
}
